import React, { useState } from 'react';
import { 
  Download, 
  FileText, 
  Calendar, 
  Filter,
  Loader,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';

const LogExporter = ({ logs = [], filters = {}, isVisible, onClose }) => {
  const [exportFormat, setExportFormat] = useState('csv');
  const [dateRange, setDateRange] = useState({
    start: format(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'), // 7 days ago
    end: format(new Date(), 'yyyy-MM-dd')
  });
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [includeChanges, setIncludeChanges] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);

  if (!isVisible) return null;

  const generateCSV = (logsToExport) => {
    const headers = [
      'Timestamp',
      'User Email',
      'Action',
      'Resource',
      'Resource ID',
      'Severity',
      'Description'
    ];

    if (includeChanges) {
      headers.push('Fields Changed', 'Changes Summary');
    }

    if (includeMetadata) {
      headers.push('IP Address', 'User Agent', 'Session ID', 'Source');
    }

    const csvRows = [headers.join(',')];

    logsToExport.forEach(log => {
      const row = [
        `"${log.timestamp.toDate().toISOString()}"`,
        `"${log.userEmail || ''}"`,
        `"${log.action || ''}"`,
        `"${log.resource || ''}"`,
        `"${log.resourceId || ''}"`,
        `"${log.severity || ''}"`,
        `"${log.description || ''}"`
      ];

      if (includeChanges) {
        const fieldsChanged = log.changes?.fieldsChanged?.join('; ') || '';
        const changesSummary = log.changes?.fieldsChanged?.map(field => {
          const before = log.changes.before?.[field] || '';
          const after = log.changes.after?.[field] || '';
          return `${field}: "${before}" → "${after}"`;
        }).join('; ') || '';
        
        row.push(`"${fieldsChanged}"`, `"${changesSummary}"`);
      }

      if (includeMetadata) {
        row.push(
          `"${log.metadata?.ipAddress || ''}"`,
          `"${log.metadata?.userAgent || ''}"`,
          `"${log.metadata?.sessionId || ''}"`,
          `"${log.metadata?.source || ''}"`
        );
      }

      csvRows.push(row.join(','));
    });

    return csvRows.join('\n');
  };

  const generateJSON = (logsToExport) => {
    return JSON.stringify(logsToExport.map(log => ({
      ...log,
      timestamp: log.timestamp.toDate().toISOString()
    })), null, 2);
  };

  const filterLogsByDateRange = (logs) => {
    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    endDate.setHours(23, 59, 59, 999); // Include the entire end date

    return logs.filter(log => {
      const logDate = log.timestamp.toDate();
      return logDate >= startDate && logDate <= endDate;
    });
  };

  const getFilteredLogs = () => {
    let filteredLogs = [...logs];

    // Apply date range filter
    filteredLogs = filterLogsByDateRange(filteredLogs);

    // Apply existing filters if any
    if (filters.action && filters.action !== 'all') {
      filteredLogs = filteredLogs.filter(log => log.action === filters.action);
    }
    
    if (filters.resource && filters.resource !== 'all') {
      filteredLogs = filteredLogs.filter(log => log.resource === filters.resource);
    }
    
    if (filters.user && filters.user !== 'all') {
      filteredLogs = filteredLogs.filter(log => log.userEmail === filters.user);
    }
    
    if (filters.severity && filters.severity !== 'all') {
      filteredLogs = filteredLogs.filter(log => log.severity === filters.severity);
    }

    return filteredLogs;
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    setIsExporting(true);
    setExportStatus(null);

    try {
      const filteredLogs = getFilteredLogs();
      
      if (filteredLogs.length === 0) {
        setExportStatus({
          type: 'warning',
          message: 'No logs found for the selected criteria.'
        });
        setIsExporting(false);
        return;
      }

      const timestamp = format(new Date(), 'yyyy-MM-dd_HH-mm-ss');
      let content, filename, mimeType;

      if (exportFormat === 'csv') {
        content = generateCSV(filteredLogs);
        filename = `audit_logs_${timestamp}.csv`;
        mimeType = 'text/csv';
      } else if (exportFormat === 'json') {
        content = generateJSON(filteredLogs);
        filename = `audit_logs_${timestamp}.json`;
        mimeType = 'application/json';
      }

      // Simulate processing time for large exports
      if (filteredLogs.length > 1000) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      downloadFile(content, filename, mimeType);
      
      setExportStatus({
        type: 'success',
        message: `Successfully exported ${filteredLogs.length} logs as ${filename}`
      });

    } catch (error) {
      console.error('Export failed:', error);
      setExportStatus({
        type: 'error',
        message: 'Export failed. Please try again.'
      });
    } finally {
      setIsExporting(false);
    }
  };

  const filteredLogsCount = getFilteredLogs().length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Download className="w-5 h-5 mr-2" />
            Export Audit Logs
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Export Format */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Export Format
          </label>
          <div className="flex space-x-3">
            <label className="flex items-center">
              <input
                type="radio"
                value="csv"
                checked={exportFormat === 'csv'}
                onChange={(e) => setExportFormat(e.target.value)}
                className="mr-2"
              />
              <FileText className="w-4 h-4 mr-1" />
              CSV
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="json"
                checked={exportFormat === 'json'}
                onChange={(e) => setExportFormat(e.target.value)}
                className="mr-2"
              />
              <FileText className="w-4 h-4 mr-1" />
              JSON
            </label>
          </div>
        </div>

        {/* Date Range */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="w-4 h-4 inline mr-1" />
            Date Range
          </label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">From</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">To</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Export Options */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Include Additional Data
          </label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeChanges}
                onChange={(e) => setIncludeChanges(e.target.checked)}
                className="mr-2"
              />
              Change details and field modifications
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeMetadata}
                onChange={(e) => setIncludeMetadata(e.target.checked)}
                className="mr-2"
              />
              Metadata (IP address, user agent, session)
            </label>
          </div>
        </div>

        {/* Current Filters Info */}
        {(filters.action !== 'all' || filters.resource !== 'all' || filters.user !== 'all' || filters.severity !== 'all') && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center text-sm text-blue-700">
              <Filter className="w-4 h-4 mr-1" />
              Active filters will be applied to export
            </div>
          </div>
        )}

        {/* Records Count */}
        <div className="mb-4 text-sm text-gray-600">
          {filteredLogsCount} logs will be exported
        </div>

        {/* Status Message */}
        {exportStatus && (
          <div className={`mb-4 p-3 rounded-md flex items-center ${
            exportStatus.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
            exportStatus.type === 'warning' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
            'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {exportStatus.type === 'success' && <CheckCircle className="w-4 h-4 mr-2" />}
            {exportStatus.type === 'warning' && <AlertCircle className="w-4 h-4 mr-2" />}
            {exportStatus.type === 'error' && <AlertCircle className="w-4 h-4 mr-2" />}
            {exportStatus.message}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting || filteredLogsCount === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
          >
            {isExporting ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogExporter;
